import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import Button from "../../../../../shared/ui/button/Button";
import { Modal } from "../../../../../shared/ui/modal";
import { useCenter } from "../../../../center";
import StudentCourseFields from "../StudentCourseFields";
import PersonFields from "../PersonFields";
import { updatePatient } from "../../../application/patients";
import { validatePatient, validatePatientField } from "../../../domain/patientValidators";
import { personDetailsFrom, type PersonDetails, type PersonField } from "../../../domain/PersonDetails";
import { refusesWhatsApp, type Patient, type UpdatePatientDTO } from "../../../domain/Patient";
import { whatsappNumberForForm } from "../../../../../shared/domain/whatsappNumber";

interface EditPatientModalProps {
    isOpen: boolean;
    onClose: () => void;
    patient: Patient;
    onSuccess: () => void;
    therapistID: string;
}

export default function EditPatientModal({
    isOpen,
    onClose,
    patient,
    onSuccess,
    therapistID,
}: EditPatientModalProps) {
    const { t } = useTranslation();
    const [data, setFormData] = useState<PersonDetails>(() => personDetailsFrom(patient));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const { center } = useCenter();
    const isStudent =
        center?.type === "training" && Boolean(patient?.center_id) && patient?.center_id === center.id;
    const [courseId, setCourseId] = useState("");
    const [academicYear, setAcademicYear] = useState("");

    useEffect(() => {
        if (patient) {
            setFormData(personDetailsFrom(patient));
            setCourseId(patient.course_id ?? "");
            setAcademicYear(patient.academic_year ? String(patient.academic_year) : "");
            setFieldErrors({});
        }
    }, [patient]);

    const handleChange = (changes: Partial<PersonDetails>) => {
        setFormData((prev) => ({ ...prev, ...changes }));
    };

    // Each field is checked as it is left, and the save waits for them all.
    const handleBlur = (field: PersonField, next: PersonDetails) => {
        const errKey = validatePatientField(field, next);
        setFieldErrors((prev) => {
            const errors = { ...prev };
            if (errKey) errors[field] = errKey;
            else delete errors[field];
            return errors;
        });
    };

    const hasErrors = Object.keys(fieldErrors).length > 0;

    const handleSubmit = async () => {
        const errors = validatePatient(data);
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const record = {
                ...data,
                therapist_id: patient.therapist_id || therapistID,
            };
            const payload: Record<string, unknown> = Object.fromEntries(
                Object.entries(record).filter(([, value]) => value !== "")
            );
            // An emptied timezone is a change, not an omission: it means "back
            // to the therapist's zone", and the filter above would drop it.
            payload.timezone = data.timezone || null;
            // Emptied, it is cleared; typed nationally, it gets the country code.
            payload.whatsapp_phone = whatsappNumberForForm(data.whatsapp_phone, data.country) ?? null;
            if (isStudent) {
                // Sent as null too: taking somebody out of a course is a change.
                payload.course_id = courseId || null;
                payload.academic_year = courseId && academicYear ? Number(academicYear) : null;
            }

            await updatePatient(patient.id, payload as UpdatePatientDTO);
            onSuccess();
            onClose();
        } catch (err: unknown) {
            console.error(err);
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            setError(
                detail === "course_needs_center_student" || detail === "course_not_in_center"
                    ? t("patient.course.error")
                    : t("patient.saveError")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            ariaLabel={t("patient.editPatientDetails")}
            className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border-subtle bg-surface-raised shadow-modal"
        >
            {/* A real form, so Enter in any field saves as the Save button does. */}
            <form
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-border-subtle bg-surface-raised/90 sticky top-0 z-10 backdrop-blur-md">
                    <h2 className="text-xl font-bold text-content-primary">
                        {t("patient.editPatientDetails")}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close")}
                        className="p-2 rounded-full hover:bg-surface-sunken transition-colors text-content-tertiary hover:text-content-primary"
                    >
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>

                <div className="p-6 space-y-8">
                    <PersonFields
                        idPrefix="edit-patient"
                        value={data}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={fieldErrors}
                        refusedWhatsApp={refusesWhatsApp(patient)}
                    />

                    {isStudent && center && (
                        <div className="space-y-3">
                            <h3 className="text-sm font-semibold text-content-primary">
                                {t("patient.course.title")}
                            </h3>
                            <StudentCourseFields
                                centerId={center.id}
                                courseId={courseId}
                                year={academicYear}
                                onCourseChange={setCourseId}
                                onYearChange={setAcademicYear}
                                disabled={loading}
                            />
                        </div>
                    )}

                    {/* Always rendered, so a screen reader announces the message when it appears. */}
                    <div role="alert" aria-live="assertive">
                        {error && (
                            <p className="text-status-critical-on-tint text-sm text-center bg-status-critical-tint p-2 rounded-lg">
                                {error}
                            </p>
                        )}
                    </div>
                </div>

                <div className="border-t border-border-subtle p-6 flex justify-end gap-3 bg-surface-raised/90 sticky bottom-0 backdrop-blur-md rounded-b-2xl">
                    <Button variant="outline" onClick={onClose} disabled={loading} className="rounded-xl px-6">
                        {t("common.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        loading={loading}
                        disabled={hasErrors}
                        className="rounded-xl px-8 shadow-lg shadow-brand-500/20"
                    >
                        {loading ? t("common.loading") : t("common.save")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
